import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppHeader } from "@/components/AppHeader";
import { DateField } from "@/components/DateField";
import { api, formatApiErrorDetail, downloadFile } from "@/lib/api";

const KINDS = [
  ["slab", "Slab"],
  ["remnant", "Remnant"],
  ["sink", "Sink"],
  ["other", "Other"],
];
const RESOLUTIONS = ["Credit", "Replacement", "Refund", "Rejected"];
const ENDPOINT = { slab: "/slabs", remnant: "/remnants", sink: "/sinks" };
const labelOf = (kind, r) =>
  kind === "sink"
    ? [r.brand, r.model].filter(Boolean).join(" ")
    : r.slab_color || r.color || "";
const sizeOf = (r) =>
  r.length_in && r.width_in ? `${r.length_in}" x ${r.width_in}"` : "";

const EMPTY = {
  supplier: "",
  supplier_id: "",
  po_number: "",
  manufacturer_rma: "",
  reason: "",
  resolution: "",
  credit_amount: "",
  notes: "",
  pickup_date: "",
  credit_note: "",
  items: [],
};
const EMPTY_ITEM = {
  kind: "slab", item_id: "", code: "", label: "", quantity: 1,
  thickness: "", size: "", lot_number: "", serial_number: "", supplier: "",
};

const labelCls = "text-xs uppercase tracking-widest text-zinc-400";
const inputCls = "h-12 border-zinc-800 bg-zinc-900 text-base";

function ItemRow({ row, idx, onChange, onRemove }) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const timer = useRef(null);

  const runSearch = (t) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (row.kind === "other" || !t.trim()) {
        setResults([]);
        return;
      }
      try {
        const { data } = await api.get(ENDPOINT[row.kind], {
          params: { q: t.trim() },
        });
        setResults((data || []).slice(0, 8));
      } catch {
        setResults([]);
      }
    }, 250);
  };

  const pick = (r) => {
    onChange({
      ...row,
      item_id: r.id,
      code: r.code || "",
      label: labelOf(row.kind, r),
      thickness: r.thickness || "",
      size: sizeOf(r),
      lot_number: r.lot_number || "",
      serial_number: r.serial_number || "",
      supplier: r.supplier || r.brand || "",
    });
    setResults([]);
    setTerm("");
  };

  return (
    <div
      className="rounded-md border border-zinc-800 bg-zinc-950 p-3"
      data-testid={`rma-item-row-${idx}`}
    >
      <div className="grid gap-3 sm:grid-cols-[130px,1fr,80px,auto] sm:items-end">
        <div className="space-y-1">
          <Label className={labelCls}>Type</Label>
          <Select
            value={row.kind}
            onValueChange={(v) =>
              onChange({ ...row, kind: v, item_id: "", code: "", label: "" })
            }
          >
            <SelectTrigger
              data-testid={`rma-item-kind-${idx}`}
              className="h-11 border-zinc-800 bg-zinc-900 text-sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-zinc-800 bg-zinc-900">
              {KINDS.map(([v, l]) => (
                <SelectItem key={v} value={v}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="relative space-y-1">
          <Label className={labelCls}>Item</Label>
          {row.kind === "other" ? (
            <Input
              data-testid={`rma-item-label-${idx}`}
              value={row.label}
              onChange={(e) => onChange({ ...row, label: e.target.value })}
              placeholder="Describe the returned item"
              className="h-11 border-zinc-800 bg-zinc-900 text-sm"
            />
          ) : row.item_id ? (
            <div className="flex h-11 items-center justify-between rounded-md border border-zinc-800 bg-zinc-900 px-3 text-sm">
              <span className="truncate">
                <span className="font-mono-data mr-2 text-yellow-500">
                  {row.code}
                </span>
                {row.label}
                {(row.thickness || row.size) && (
                  <span className="ml-2 text-xs text-zinc-500">
                    {[row.thickness, row.size].filter(Boolean).join(" · ")}
                  </span>
                )}
              </span>
              <button
                type="button"
                data-testid={`rma-item-change-${idx}`}
                onClick={() =>
                  onChange({ ...row, item_id: "", code: "", label: "" })
                }
                className="ml-2 shrink-0 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white"
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <Input
                data-testid={`rma-item-search-${idx}`}
                value={term}
                onChange={(e) => {
                  setTerm(e.target.value);
                  runSearch(e.target.value);
                }}
                placeholder={`Search ${row.kind}s by color or code`}
                className="h-11 border-zinc-800 bg-zinc-900 text-sm"
              />
              {results.length > 0 && (
                <div className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-zinc-800 bg-zinc-900 shadow-xl">
                  {results.map((r) => (
                    <button
                      type="button"
                      key={r.id}
                      data-testid={`rma-item-opt-${idx}-${r.id}`}
                      onClick={() => pick(r)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-800"
                    >
                      <span className="font-mono-data text-xs text-yellow-500">
                        {r.code}
                      </span>
                      <span className="truncate">{labelOf(row.kind, r)}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="space-y-1">
          <Label className={labelCls}>Qty</Label>
          <Input
            data-testid={`rma-item-qty-${idx}`}
            type="number"
            min="1"
            value={row.quantity}
            onChange={(e) => onChange({ ...row, quantity: e.target.value })}
            className="h-11 border-zinc-800 bg-zinc-900 text-sm"
          />
        </div>

        <button
          type="button"
          data-testid={`rma-item-remove-${idx}`}
          onClick={onRemove}
          className="flex h-11 w-11 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 transition-colors duration-150 hover:border-red-500/40 hover:text-red-400"
        >
          <Trash size={16} weight="bold" />
        </button>
      </div>

      {row.kind !== "other" && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid={`rma-item-tracking-${idx}`}>
          <div className="space-y-1">
            <Label className={labelCls}>Lot #</Label>
            <Input
              data-testid={`rma-item-lot-${idx}`}
              value={row.lot_number || ""}
              onChange={(e) => onChange({ ...row, lot_number: e.target.value })}
              placeholder="e.g. LOT-7742"
              className="h-11 border-zinc-800 bg-zinc-900 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className={labelCls}>Serial #</Label>
            <Input
              data-testid={`rma-item-serial-${idx}`}
              value={row.serial_number || ""}
              onChange={(e) => onChange({ ...row, serial_number: e.target.value })}
              placeholder="e.g. SN-0098231"
              className="h-11 border-zinc-800 bg-zinc-900 text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function RMAForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [supTerm, setSupTerm] = useState("");
  const [supOpen, setSupOpen] = useState(false);

  useEffect(() => {
    api.get("/suppliers").then(({ data }) => setSuppliers(data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    const { data } = await api.get(`/rmas/${id}`);
    setForm({
      ...EMPTY,
      ...data,
      credit_amount: data.credit_amount ?? "",
      resolution: data.resolution || "",
      items: (data.items || []).map((it) => ({ ...EMPTY_ITEM, ...it, quantity: it.quantity || 1 })),
    });
    setSupTerm(data.supplier || "");
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // "Start return" from a slab page: ?slab=<id>&print=1 pre-fills the item + supplier
  const [params] = useSearchParams();
  const fromSlab = params.get("slab");
  const autoPrint = params.get("print") === "1";
  useEffect(() => {
    if (id || !fromSlab) return;
    api
      .get(`/slabs/${fromSlab}`)
      .then(({ data: s }) => {
        setForm((f) => ({
          ...f,
          supplier: f.supplier || s.supplier || "",
          items: f.items.length
            ? f.items
            : [{
                ...EMPTY_ITEM,
                kind: "slab",
                item_id: s.id,
                code: s.code || "",
                label: s.slab_color || "",
                thickness: s.thickness || "",
                size: sizeOf(s),
                lot_number: s.lot_number || "",
                serial_number: s.serial_number || "",
                supplier: s.supplier || "",
              }],
        }));
        if (s.supplier) setSupTerm(s.supplier);
      })
      .catch(() => toast.error("Could not load that slab"));
  }, [id, fromSlab]);

  useEffect(() => {
    if (!fromSlab || !suppliers.length) return;
    setForm((f) =>
      f.supplier && !f.supplier_id
        ? { ...f, supplier_id: suppliers.find((s) => s.name === f.supplier)?.id || "" }
        : f,
    );
  }, [fromSlab, suppliers]);

  const setSupplier = (name, sid = "") => {
    setSupTerm(name);
    setForm((f) => ({ ...f, supplier: name, supplier_id: sid }));
  };
  const supMatches = suppliers.filter(
    (s) => !supTerm.trim() || s.name.toLowerCase().includes(supTerm.trim().toLowerCase()),
  );

  const addItem = () =>
    setForm((f) => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  // the first slab added tells us who to send it back to
  const changeItem = (i, next) =>
    setForm((f) => {
      const items = f.items.map((it, n) => (n === i ? next : it));
      const patch = {};
      if (!f.supplier && next.supplier && next.item_id && !f.items[i]?.item_id) {
        patch.supplier = next.supplier;
        patch.supplier_id = suppliers.find((s) => s.name === next.supplier)?.id || "";
        setSupTerm(next.supplier);
      }
      return { ...f, items, ...patch };
    });
  const removeItem = (i) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, n) => n !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      supplier: form.supplier || "",
      supplier_id: form.supplier_id || "",
      po_number: form.po_number || "",
      manufacturer_rma: form.manufacturer_rma || "",
      reason: form.reason || "",
      resolution: form.resolution || "",
      credit_amount: form.credit_amount === "" ? null : Number(form.credit_amount),
      notes: form.notes || "",
      pickup_date: form.pickup_date || "",
      credit_note: form.credit_note || "",
      items: form.items
        .filter((it) => (it.kind === "other" ? it.label.trim() : it.item_id))
        .map(({ supplier, ...it }) => ({ ...it, quantity: Number(it.quantity) || 1 })),
    };
    try {
      if (id) {
        await api.put(`/rmas/${id}`, payload);
        toast.success("Return updated");
        navigate(`/inventory/rmas/${id}`);
      } else {
        const { data } = await api.post("/rmas", payload);
        toast.success("Return created");
        if (autoPrint) {
          try {
            await downloadFile(`/rmas/${data.id}/labels.pdf`, `${data.code}-label-4x6.pdf`);
          } catch {
            toast.error("Return saved, but the label could not be built");
          }
        }
        navigate(`/inventory/rmas/${data.id}`);
      }
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save the return",
      );
      setBusy(false);
    }
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title={id ? "Inventory / Edit Return" : "Inventory / New Return"} />
      <main className="relative z-10 pb-28 pt-6">
        <Link
          to={id ? `/inventory/rmas/${id}` : "/inventory/rmas"}
          data-testid="back-to-rmas"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> {id ? "Back to return" : "All returns"}
        </Link>
        <h1 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
          {id ? "Edit Return" : "New Return (RMA)"}
        </h1>

        <form
          onSubmit={submit}
          data-testid="rma-form"
          className="mt-6 max-w-3xl space-y-6"
        >
          <div className="grid gap-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4 sm:grid-cols-2">
            <div className="relative space-y-2 sm:col-span-2">
              <Label className={labelCls}>Supplier (who it goes back to)</Label>
              <Input
                data-testid="rma-supplier-input"
                value={supTerm}
                onChange={(e) => {
                  setSupplier(e.target.value);
                  setSupOpen(true);
                }}
                onFocus={() => setSupOpen(true)}
                onBlur={() => setTimeout(() => setSupOpen(false), 150)}
                placeholder="Pick a supplier, or type a new name"
                className={inputCls}
              />
              {supOpen && supMatches.length > 0 && (
                <div className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-zinc-800 bg-zinc-900 shadow-xl">
                  {supMatches.slice(0, 8).map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      data-testid={`rma-supplier-opt-${s.id}`}
                      onMouseDown={() => setSupplier(s.name, s.id)}
                      className="flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-zinc-800"
                    >
                      <span className="font-semibold">{s.name}</span>
                      {(s.contact || s.email) && (
                        <span className="text-xs text-zinc-500">
                          {[s.contact, s.email].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Supplier RMA #</Label>
              <Input
                data-testid="rma-manufacturer-input"
                value={form.manufacturer_rma}
                onChange={(e) =>
                  setForm({ ...form, manufacturer_rma: e.target.value })
                }
                placeholder="Issued by the supplier, e.g. MSI-88213"
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Supplier PO / invoice # (optional)</Label>
              <Input
                data-testid="rma-po-input"
                value={form.po_number}
                onChange={(e) => setForm({ ...form, po_number: e.target.value })}
                placeholder="PO-2231"
                className={inputCls}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className={labelCls}>Reason for return</Label>
              <Textarea
                data-testid="rma-reason-input"
                rows={2}
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Cracked on delivery, wrong color, resin pits, off-spec thickness…"
                className="border-zinc-800 bg-zinc-950"
              />
            </div>
          </div>

          <div className="space-y-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
            <div className="flex items-center justify-between">
              <Label className={labelCls}>Items going back</Label>
              <Button
                type="button"
                data-testid="rma-add-item"
                onClick={addItem}
                variant="outline"
                className="h-9 border-zinc-700 bg-transparent text-[11px] uppercase tracking-widest"
              >
                <Plus size={14} weight="bold" className="mr-1.5" /> Add item
              </Button>
            </div>
            {form.items.length === 0 && (
              <p className="text-sm text-zinc-500">
                Add each slab, remnant or sink going back. They leave inventory
                automatically once the return is marked shipped.
              </p>
            )}
            <div className="space-y-3">
              {form.items.map((row, i) => (
                <ItemRow
                  key={i}
                  row={row}
                  idx={i}
                  onChange={(next) => changeItem(i, next)}
                  onRemove={() => removeItem(i)}
                />
              ))}
            </div>
          </div>

          <div className="grid gap-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className={labelCls}>Resolution</Label>
              <Select
                value={form.resolution || "none"}
                onValueChange={(v) =>
                  setForm({ ...form, resolution: v === "none" ? "" : v })
                }
              >
                <SelectTrigger
                  data-testid="rma-resolution-select"
                  className={inputCls}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  <SelectItem value="none">Not decided</SelectItem>
                  {RESOLUTIONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Credit / refund expected ($)</Label>
              <Input
                data-testid="rma-credit-input"
                type="number"
                step="0.01"
                min="0"
                value={form.credit_amount}
                onChange={(e) =>
                  setForm({ ...form, credit_amount: e.target.value })
                }
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Supplier pickup date</Label>
              <DateField
                testid="rma-pickup-date-input"
                value={form.pickup_date || ""}
                onChange={(d) => setForm({ ...form, pickup_date: d })}
                placeholder="Pick a date"
              />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Supplier credit note #</Label>
              <Input
                data-testid="rma-credit-note-input"
                value={form.credit_note || ""}
                onChange={(e) => setForm({ ...form, credit_note: e.target.value })}
                placeholder="CN-40921"
                className={inputCls}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className={labelCls}>Internal notes</Label>
              <Textarea
                data-testid="rma-notes-input"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Anything the team should know about this return…"
                className="border-zinc-800 bg-zinc-950"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              type="submit"
              data-testid="save-rma-button"
              disabled={busy}
              className="h-12 flex-1 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              {busy ? "Saving…" : id ? "Save Return" : "Create Return"}
            </Button>
            <Button
              type="button"
              data-testid="cancel-rma-button"
              variant="outline"
              onClick={() =>
                navigate(id ? `/inventory/rmas/${id}` : "/inventory/rmas")
              }
              className="h-12 border-zinc-700 bg-transparent text-xs uppercase tracking-widest"
            >
              Cancel
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
